import { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useDevAgentStore } from "@/stores/devAgentStore";
import { useAuthStoreV1 } from "@/stores/authStore_v1";
import { fetchActiveBranch } from "@/hooks/metadata_v1/useActiveBranch";
import {
  getMetadataItemById,
  getMetadataItemByName,
} from "../devAgentService";
import type { MetadataItem } from "../devAgentService";
import type { ParsedFenceMeta } from "./apexRendererMeta";

interface UseApexCodeActionsParams {
  codeString: string;
  compareName?: string;
  fenceMeta: ParsedFenceMeta;
  fileId: string;
  metadataType: string;
}

export function useApexCodeActions({
  codeString,
  compareName,
  fenceMeta,
  fileId,
  metadataType,
}: UseApexCodeActionsParams) {
  type SecondaryActionKind = "compare" | "create";
  const [secondaryAction, setSecondaryAction] = useState<"compare" | "create" | "hidden">(() => {
    if (fenceMeta.operation === "created") return "create";
    if (fenceMeta.metadataId || compareName) return "compare";
    return "hidden";
  });

  const displayName = useMemo(
    () =>
      compareName ||
      fenceMeta.artifactName ||
      `artifact-${codeString.slice(0, 8).replace(/\s/g, "")}`,
    [codeString, compareName, fenceMeta.artifactName],
  );

  const virtualFileId = useMemo(() => {
    if (fenceMeta.artifactId) return fenceMeta.artifactId;
    if (!fenceMeta.metadataId && fileId && fileId !== "artifact") return fileId;
    const safeName = displayName.replace(/[^A-Za-z0-9_.-]+/g, "_");
    return `virtual:${metadataType}:${safeName}`;
  }, [displayName, fenceMeta.artifactId, fenceMeta.metadataId, fileId, metadataType]);

  const openEditorWithCode = useCallback(
    ({
      id,
      name,
      badge,
    }: {
      id: string;
      name: string;
      badge: "R" | "C";
    }) => {
      const store = useDevAgentStore.getState();
      store.addVirtualFile(id, { content: codeString, metadataType, name });
      store.setEditorOpen(true);
      store.setPendingFile({ id, name, metadataType, content: codeString });
      store.addToArtifacts({ id, name, metadataType, content: codeString });
      store.setArtifactBadge(id, badge);
      return store;
    },
    [codeString, metadataType],
  );

  const resolveOrgTarget = useCallback(async (): Promise<MetadataItem | null> => {
    if (fenceMeta.operation === "created") return null;
    const orgId = useAuthStoreV1.getState().selectedOrg?.id || "";
    if (!orgId) return null;
    try {
      if (fenceMeta.metadataId) {
        return await getMetadataItemById(fenceMeta.metadataId, orgId);
      }
      if (compareName) {
        return await getMetadataItemByName(compareName, orgId, true);
      }
    } catch {
      return null;
    }
    return null;
  }, [compareName, fenceMeta.metadataId, fenceMeta.operation]);

  useEffect(() => {
    let cancelled = false;
    if (fenceMeta.operation === "created") {
      setSecondaryAction("create");
      return () => {
        cancelled = true;
      };
    }
    if (!fenceMeta.metadataId && !compareName) {
      setSecondaryAction("hidden");
      return () => {
        cancelled = true;
      };
    }
    void (async () => {
      const item = await resolveOrgTarget();
      if (cancelled) return;
      setSecondaryAction(item ? "compare" : "create");
    })();
    return () => {
      cancelled = true;
    };
  }, [compareName, fenceMeta.metadataId, fenceMeta.operation, resolveOrgTarget]);

  const handleAddToWorkspace = useCallback(() => {
    void (async () => {
      const existing = await resolveOrgTarget();
      const resolvedName =
        compareName ||
        existing?.name ||
        existing?.full_name ||
        existing?.file_name ||
        displayName;
      const id = existing?.id || virtualFileId;
      const badge: "R" | "C" = existing?.id ? "R" : "C";
      openEditorWithCode({ id, name: resolvedName, badge });
      toast.success(`Added ${resolvedName} to workspace`);
    })();
  }, [compareName, displayName, openEditorWithCode, resolveOrgTarget, virtualFileId]);

  const handleCreateNew = useCallback(() => {
    openEditorWithCode({
      id: virtualFileId,
      name: displayName,
      badge: "C",
    });
    toast.success(`Opened ${displayName} as a new ${metadataType}`);
  }, [displayName, metadataType, openEditorWithCode, virtualFileId]);

  const handleCompare = useCallback(async () => {
    const existing = await resolveOrgTarget();
    if (!existing?.id) {
      handleCreateNew();
      return;
    }
    const store = useDevAgentStore.getState();
    try {
      const resolvedName =
        compareName ||
        existing.name ||
        existing.full_name ||
        existing.file_name ||
        displayName;
      const resolvedId = existing.id;
      let baseline = existing.content || "";
      if (resolvedId) {
        try {
          const branch = await fetchActiveBranch({ metadataItemId: resolvedId });
          if (branch) {
            baseline = branch.content ?? baseline;
          }
        } catch {
          /* keep main baseline */
        }
      }
      store.addVirtualFile(resolvedId, {
        content: baseline,
        metadataType: existing.metadata_type || metadataType,
        name: resolvedName,
      });
      store.setPendingFile({
        id: resolvedId,
        name: resolvedName,
        metadataType: existing.metadata_type || metadataType,
        content: baseline,
      });
      store.setEditorOpen(true);
      store.setAcceptedCode(codeString, resolvedName);
      store.setArtifactBadge(resolvedId, "R");
    } catch {
      handleCreateNew();
    }
  }, [
    codeString,
    compareName,
    displayName,
    handleCreateNew,
    metadataType,
    resolveOrgTarget,
  ]);

  const secondaryActionLabel =
    secondaryAction === "compare" ? "Compare" : "Create New";
  const secondaryActionTitle =
    secondaryAction === "compare"
      ? "Compare with original from org"
      : "Open this generated file as a new metadata item";
  const secondaryActionKind: SecondaryActionKind =
    secondaryAction === "compare" ? "compare" : "create";

  return {
    handleAddToWorkspace,
    handleCompare,
    showSecondaryAction: secondaryAction !== "hidden",
    secondaryActionKind,
    secondaryActionLabel,
    secondaryActionTitle,
  };
}
